import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Check, Building2, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedWidgets from '../components/FixedWidgets';

const InsurancePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const privateInsurances = [
    { name: 'Star Health Insurance', type: 'Cashless' },
    { name: 'ICICI Lombard', type: 'Cashless' },
    { name: 'SBI General Insurance', type: 'Cashless' },
    { name: 'TATA AIG', type: 'Cashless' },
    { name: 'HDFC ERGO', type: 'Cashless' },
    { name: 'New India Assurance', type: 'Cashless' },
    { name: 'Oriental Insurance', type: 'Cashless' },
    { name: 'National Insurance', type: 'Cashless' },
    { name: 'United India Insurance', type: 'Non-Cashless' },
    { name: 'Bajaj Allianz', type: 'Non-Cashless' },
    { name: 'Care Health Insurance', type: 'Cashless' },
    { name: 'Max Bupa Health', type: 'Non-Cashless' },
    { name: 'Reliance General Insurance', type: 'Non-Cashless' },
    { name: 'Aditya Birla Health', type: 'Cashless' },
    { name: 'Cholamandalam MS', type: 'Non-Cashless' }
  ];

  const governmentSchemes = [
    { name: 'Ayushman Bharat (PMJAY)', icon: Users },
    { name: 'Karnataka Arogya Bhagya (Suvarna Arogya Suraksha)', icon: Shield },
    { name: 'CGHS (Central Government Health Scheme)', icon: Building2 },
    { name: 'ECHS (Ex-Servicemen Contributory Health Scheme)', icon: Shield },
    { name: 'ESI (Employee State Insurance)', icon: Users }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FixedWidgets />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden mt-20">
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 to-[#4A90A4]/80 z-10" />
          <div className="absolute inset-0 bg-[#1e3a5f]" />
        </motion.div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold text-white mb-6"
          >
            Insurance <span className="text-[#6FCF97]">Partners</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-200 max-w-3xl mx-auto"
          >
            We accept multiple insurance providers for your convenience
          </motion.p>
        </div>
      </section>

      {/* Private Insurance Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Private <span className="text-[#4A90A4]">Insurance Partners</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Leading private insurance companies we work with
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {privateInsurances.map((insurance, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      insurance.type === 'Cashless' 
                        ? 'bg-gradient-to-br from-[#4A90A4] to-[#6FCF97]' 
                        : 'bg-gradient-to-br from-gray-400 to-gray-500'
                    }`}>
                      <Shield className="text-white" size={24} />
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    insurance.type === 'Cashless'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {insurance.type}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{insurance.name}</h3>
                {insurance.type === 'Cashless' && (
                  <div className="flex items-center space-x-2 text-sm text-green-600">
                    <Check size={16} />
                    <span>Instant approval available</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Government Insurance Section */}
      <section className="py-20 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a7b] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Government <span className="text-[#6FCF97]">Insurance Schemes</span>
            </h2>
            <p className="text-gray-300 text-lg">
              Empanelled with major government health schemes
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {governmentSchemes.map((scheme, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.05 }}
                className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="w-16 h-16 bg-[#6FCF97] rounded-2xl flex items-center justify-center mb-6"
                >
                  <scheme.icon className="text-white" size={32} />
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-3">{scheme.name}</h3>
                <div className="flex items-center space-x-2 text-[#6FCF97]">
                  <Check size={20} />
                  <span className="text-sm">Fully Empanelled</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <motion.div
          {...fadeInUp}
          className="max-w-4xl mx-auto px-6 text-center"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Need Help with <span className="text-[#4A90A4]">Insurance Claims?</span>
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            Our dedicated insurance desk is here to assist you with all your insurance-related queries and claims processing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="tel:+917406159333"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-[#4A90A4] to-[#6FCF97] text-white rounded-full font-semibold hover:shadow-xl transition-all duration-300"
            >
              Call Insurance Desk
            </motion.a>
            <motion.a
              href="mailto:vhlbgmpr@gmail.com"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-white text-[#4A90A4] rounded-full font-semibold border-2 border-[#4A90A4] hover:bg-[#4A90A4] hover:text-white transition-all duration-300"
            >
              Email Us
            </motion.a>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default InsurancePage;
